import { computed, getCurrentInstance, inject, provide, ref, shallowRef } from 'vue'
import type { Component, InjectionKey } from 'vue'
import ConfirmModal from '@/components/molecules/Dialog/ConfirmModal.vue'
import type { UseApiClientReturnType } from './api-client'
import type { ButtonProps } from '@/components/atoms/Button/types'

export type ConfirmModalProps = {
    title: string
    description: string
    apiCallInstance?: UseApiClientReturnType<ANY>
    testId?: string
    withReason?: boolean
    withConfirmationInput?: string
    confirmButtonProps?: ButtonProps
    cancelButtonProps?: ButtonProps
}

type ModalState = {
    opened?: boolean
    component: Component | null
    bindings?: Record<string, unknown>
    listeners?: Record<string, unknown>
}

export type ModalEntry = ModalState & { id: number }

const modalStack = ref<ModalEntry[]>([])
let lastModalId = 0

const MODAL_ENTRY_ID = Symbol('modal-entry-id') as InjectionKey<number>

const CLOSED_MODAL: ModalEntry = { id: 0, opened: false, component: null, bindings: {}, listeners: {} }

export const provideModalEntryId = (id: number) => provide(MODAL_ENTRY_ID, id)

export const closeModalById = (id: number) => {
    modalStack.value = modalStack.value.filter(entry => entry.id !== id)
}

const pushModal = (config: ModalState): number => {
    lastModalId += 1
    modalStack.value.push({ ...config, id: lastModalId, opened: true })
    return lastModalId
}

const useModal = () => {
    const ownEntryId = getCurrentInstance() ? inject(MODAL_ENTRY_ID, null) : null
    const targetId = () => ownEntryId ?? modalStack.value[modalStack.value.length - 1]?.id

    const modalState = computed<ModalEntry>(() => {
        const id = targetId()
        return modalStack.value.find(entry => entry.id === id) ?? CLOSED_MODAL
    })

    const hideModal = () => {
        const id = targetId()
        if (id !== undefined) closeModalById(id)
    }

    const openModal = (config: ModalState) => pushModal(config)

    const replaceModal = (config: ModalState) => {
        hideModal()
        return pushModal(config)
    }

    const confirmModal = (props: ConfirmModalProps) => {
        return new Promise<string | undefined>((resolve, reject) => {
            const id = pushModal({
                component: shallowRef(ConfirmModal),
                bindings: props,
                listeners: {
                    success: (reason?: string) => {
                        closeModalById(id)
                        resolve(reason)
                    },
                    cancel: () => {
                        closeModalById(id)
                        reject()
                    },
                },
            })
        })
    }

    return { modalStack, modalState, openModal, replaceModal, hideModal, confirmModal }
}

export default useModal
